import {
  MEAL_COUNT_RANGE,
  SELECTABLE_MEAL_SLOTS,
  type DayType,
  type MealSlot,
  type TrainingTimeOfDay,
} from '@cadencia/contracts';

/**
 * El esquema de comidas del usuario y su relacion con el entrenamiento
 * (docs/NUTRITION_ENGINE.md §4.3).
 *
 * Este modulo contesta a dos preguntas, y solo a esas dos:
 *
 *   1. ¿Que comidas hace esta persona? (`normalizeMealSlots`, `validateMealSlots`)
 *   2. ¿Que papel juega cada una respecto al entrenamiento de ese dia?
 *      (`workoutMealRelations`)
 *
 * El reparto de macros lo hace `meal-split.ts` con estas respuestas. Estan
 * separados porque la relacion comida-entrenamiento la necesitan tambien la
 * pantalla y el contexto de la IA, y ninguna de las dos quiere arrastrar el
 * reparto de gramos para saber cual es la comida de antes de entrenar.
 */

/**
 * Modelo de la relacion entre una comida y el entrenamiento del dia.
 *
 * EXISTE PARA QUE NO HAYA CUARENTA `if (slot === 'breakfast')` REPARTIDOS.
 *
 * El slot es una franja horaria; la relacion es lo que de verdad decide el
 * reparto. Son cosas distintas y con el esquema configurable dejan de coincidir:
 * el desayuno es la comida previa al entrenamiento de quien entrena por la
 * manana y desayuna, y es una comida normal de quien entrena por la tarde. Toda
 * regla escrita sobre el slot habria que reescribirla en cada combinacion.
 *
 * `near_workout` no es un adorno: es el caso de la comida que esta al lado del
 * entrenamiento cuando NO existe la del otro lado. Con dos comidas al dia y
 * entrenamiento de manana, la primera no es «la de antes» ni «la de despues»:
 * es la unica que hay cerca, y tiene que llevarse la cuota de las dos en vez de
 * la mitad del reparto que le tocaria por ser una de dos.
 */
export type WorkoutMealRelation =
  | 'pre_workout'
  | 'post_workout'
  | 'near_workout'
  | 'regular'
  | 'rest_day';

/**
 * Posicion de cada franja en el dia, como numero.
 *
 * No son horas y no pretenden serlo (§12 del encargo): es un EJE ORDINAL que
 * permite preguntar «que comida cae antes del entrenamiento» sin escribir la
 * tabla de todos los casos. `extra` comparte sitio con la media mañana porque
 * es donde caia cuando se generaba.
 */
const SLOT_POSITION: Record<MealSlot, number> = {
  breakfast: 0,
  midmorning: 1,
  extra: 1,
  lunch: 2,
  afternoon_snack: 3,
  dinner: 4,
};

/**
 * Donde cae el entrenamiento en ese mismo eje.
 *
 * `morning` = entre el desayuno y la media mañana. `afternoon` = entre el
 * almuerzo y la merienda. Es toda la precision que da un turno, y por eso el
 * motor no habla de «2-3 h antes» en ninguna parte: lo que sabe es el ORDEN,
 * no la distancia. El dia que exista una hora real, esta constante pasa a
 * calcularse de ella y el resto del modulo no cambia.
 */
const WORKOUT_POSITION: Record<TrainingTimeOfDay, number> = {
  morning: 0.5,
  afternoon: 2.5,
};

/** Los tipos de dia con sesion de fuerza, que son los unicos con peri-entreno. */
const DIAS_CON_FUERZA: readonly DayType[] = ['strength', 'strength_high'];

export class InvalidMealScheduleError extends Error {
  constructor(readonly reason: string) {
    super(`Esquema de comidas invalido: ${reason}`);
    this.name = 'InvalidMealScheduleError';
  }
}

/**
 * Comprueba una seleccion de comidas y la devuelve en el orden del dia.
 *
 * LA VALIDEZ NO PUEDE DEPENDER DE LA PANTALLA (§6 del encargo). El formulario
 * impide elegir cuatro cuando has dicho tres, pero por esta funcion pasan
 * tambien la importacion de datos, la sincronizacion desde otro dispositivo y
 * cualquier pantalla que se escriba mañana. Una invariante que solo vive en un
 * formulario es una invariante que ya se ha roto en alguna parte.
 *
 * Rechaza: vacio, mas de cinco, repetidos y cualquier valor que no sea una de
 * las cinco franjas —`extra` incluido, que es heredado y no elegible—.
 */
export function validateMealSlots(slots: readonly MealSlot[]): MealSlot[] {
  if (slots.length < MEAL_COUNT_RANGE.min) {
    throw new InvalidMealScheduleError('hay que elegir al menos una comida');
  }
  if (slots.length > MEAL_COUNT_RANGE.max) {
    throw new InvalidMealScheduleError(`no hay mas de ${MEAL_COUNT_RANGE.max} franjas al dia`);
  }
  const vistos = new Set<MealSlot>();
  for (const slot of slots) {
    if (!SELECTABLE_MEAL_SLOTS.includes(slot)) {
      throw new InvalidMealScheduleError(`${slot} no es una franja elegible`);
    }
    if (vistos.has(slot)) throw new InvalidMealScheduleError(`franja repetida: ${slot}`);
    vistos.add(slot);
  }
  return SELECTABLE_MEAL_SLOTS.filter((slot) => vistos.has(slot));
}

/**
 * Lo mismo, pero sin lanzar: devuelve el motivo cuando no vale.
 *
 * Lo usa la pantalla, que necesita explicar por que el boton de guardar esta
 * apagado. La version que lanza la usan el servicio y el motor, donde llegar
 * con un esquema imposible es un fallo de programacion y no una eleccion del
 * usuario a medio hacer.
 */
export function checkMealSlots(
  slots: readonly MealSlot[],
): { ok: true; slots: MealSlot[] } | { ok: false; reason: string } {
  try {
    return { ok: true, slots: validateMealSlots(slots) };
  } catch (caught) {
    if (caught instanceof InvalidMealScheduleError) return { ok: false, reason: caught.reason };
    throw caught;
  }
}

/**
 * Ordena una seleccion ya valida. Sin comprobaciones: para escribir en la base.
 *
 * El orden se normaliza ANTES de persistir para que dos dispositivos que elijan
 * las mismas tres comidas en distinto orden guarden el mismo array. Si no, la
 * sincronizacion vería un conflicto en cada guardado y no habria forma de decir
 * cual gana, porque los dos dicen lo mismo.
 */
export function normalizeMealSlots(slots: readonly MealSlot[]): MealSlot[] {
  const elegidos = new Set(slots);
  return SELECTABLE_MEAL_SLOTS.filter((slot) => elegidos.has(slot));
}

// `resolveTrainingTimeOfDay` vive en `training/session-timing.ts` desde el
// 21/09/2026: resuelve un dato de entrenamiento, no de nutricion.

export interface WorkoutMealRelationsInput {
  /** Las comidas del usuario, ya validadas. */
  slots: readonly MealSlot[];
  dayType: DayType;
  /** Turno ya resuelto. `null` = no hay sesion de fuerza o no se sabe cuando. */
  timing: TrainingTimeOfDay | null;
}

/**
 * Que papel juega cada comida del dia respecto al entrenamiento.
 *
 * LAS REGLAS, TODAS AQUI:
 *
 * 1. Sin sesion de fuerza —`rest`, `cardio` o turno desconocido— todas las
 *    comidas son `rest_day`. **No se inventan relaciones pre/post** (§17 del
 *    encargo): un dia de descanso no tiene «comida de antes de entrenar», y
 *    fabricar una solo para que el reparto tenga forma de dia de entreno seria
 *    mover hidratos por un entrenamiento que no existe. El dia de cardio se
 *    queda como estaba: su modulacion es la del §4.2 y no tiene peri-entreno.
 *
 * 2. Con sesion, el entrenamiento ocupa una posicion en el eje del dia
 *    (`WORKOUT_POSITION`). La comida anterior es la ULTIMA de las elegidas que
 *    cae antes; la posterior, la PRIMERA que cae despues.
 *
 * 3. Si solo existe una de las dos —entrenar por la mañana sin desayunar, o
 *    por la tarde sin cena— esa comida es `near_workout` y se lleva la cuota
 *    conjunta. No se crea la que falta (§15 y §16 del encargo): el esquema de
 *    comidas es del usuario y el motor no lo amplia por su cuenta.
 *
 * 4. Todo lo demas de un dia de entreno es `regular`.
 */
export function workoutMealRelations(
  input: WorkoutMealRelationsInput,
): Map<MealSlot, WorkoutMealRelation> {
  const slots = normalizeMealSlots(input.slots);
  const salida = new Map<MealSlot, WorkoutMealRelation>();

  const hayFuerza = DIAS_CON_FUERZA.includes(input.dayType);
  if (!hayFuerza || input.timing === null) {
    for (const slot of slots) salida.set(slot, 'rest_day');
    return salida;
  }

  const posicion = WORKOUT_POSITION[input.timing];
  const antes = slots.filter((slot) => SLOT_POSITION[slot] < posicion);
  const despues = slots.filter((slot) => SLOT_POSITION[slot] > posicion);

  const pre = antes.at(-1) ?? null;
  const post = despues[0] ?? null;

  for (const slot of slots) salida.set(slot, 'regular');

  if (pre !== null && post !== null) {
    salida.set(pre, 'pre_workout');
    salida.set(post, 'post_workout');
  } else if (pre !== null) {
    salida.set(pre, 'near_workout');
  } else if (post !== null) {
    salida.set(post, 'near_workout');
  }

  return salida;
}

/** La comida previa a la sesion, si la hay. Comodidad para pantallas y contexto. */
export function preWorkoutSlotOf(
  relations: ReadonlyMap<MealSlot, WorkoutMealRelation>,
): MealSlot | null {
  for (const [slot, relacion] of relations) {
    if (relacion === 'pre_workout') return slot;
  }
  return null;
}

/** La comida posterior a la sesion, si la hay. */
export function postWorkoutSlotOf(
  relations: ReadonlyMap<MealSlot, WorkoutMealRelation>,
): MealSlot | null {
  for (const [slot, relacion] of relations) {
    if (relacion === 'post_workout') return slot;
  }
  return null;
}
